/**
 * @file   src\components\BankInformation.tsx
 * @brief  Bank information component.
 * @date   JUN, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import { React, useState, Button, useEffect, useParams, useRef } from '../utils/thirdpartyComponents';
import CWSelect from '../components/CWSelect';
import CWInput from '../components/CWInput';
import { addBankInfoSchema } from '../validations/addBankInfoSchema';
import * as Yup from 'yup'
import { PaymentType } from '../utils/enums';
import {onwerbankinfoFormData, adminbankinfoFormData } from '../interfaces/generalInterface';
import { CompanyApi } from "../utils/apiUrls";
import { MessageToaster } from "../utils/toastUtil";
import axios from '../config/axiosConfig';
import FullLoader from '../components/FullLoader';
import { hasInvalidStripeSecretKey, STRIPE_SECRET_KEY_LIVE_ERROR, stripePublicKeyCwInputLabel, stripeSecretKeyCwInputLabel } from './stripePaymentInputLabels';
import { userType } from '../utils/enums';


const BankInformation = ({ onComplete, cuserType }: { onComplete: () => void; cuserType:string }) => {

    const [loading, setLoading] = useState(false);

    // Instantiate MessageToaster
    const toaster = new MessageToaster();

    // get car wash id
    const param = useParams();
    const carwashid = param.id ? parseInt(param.id) : 0;

    // Convert PaymentType enum to an array of options
    const paymentOptions = [
        { label: 'Select', value: 0 }, // First option with value 0
        ...Object.keys(PaymentType).filter(key => !isNaN(Number(key))).map(key => ({
            label: PaymentType[key as keyof typeof PaymentType],
            value: Number(key)
        }))
    ];

    const refs = useRef<{ [key: string]: HTMLInputElement | HTMLTextAreaElement | null }>({});

    // Initialize states for owner formData 
    const [formData, setFormData] = useState({
        PaymentMethod: 1,
        stripeSecretKey: '',
        stripePublicKey: '',
    });

    const [ownerformData, setOwnerFormData] = useState<onwerbankinfoFormData>({
        PaymentMethod: formData.PaymentMethod,
        paymentKeys: {
            stripeSecretKey: '',
            stripePublicKey: '',
        },
    });

    // Initialize states for admin formData 
    const [adminformData, setAdminFormData] = useState<adminbankinfoFormData>({
        carwashid: carwashid,
        PaymentMethod: formData.PaymentMethod,
        paymentKeys: {
            stripeSecretKey: '',
            stripePublicKey: '',
        },
    });

    // Initialise states for errors
    const [errors, setErrors] = useState({
        PaymentMethod: '',
        stripeSecretKey: '',
        stripePublicKey: '',
    });

    useEffect(() => {
        const savedData = localStorage.getItem('bankInformation');
        if (savedData) {
            setFormData(JSON.parse(savedData));
        }
    }, [setFormData]);

    // Triggers whenever options is selected
    const handleSelectChange = (selectedOption: any, name: string) => {

        const value = selectedOption ? selectedOption.value : '';

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    // On input change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {

        const { name, value } = e.target;

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        const newFormData = {
            ...formData,
            [name]: value,
        };

        setFormData(newFormData);
    };

    useEffect(() => {
        if(formData.PaymentMethod === 1)
        {
            if (cuserType === userType['CARWASH-OWNER']) {
                setOwnerFormData({
                    PaymentMethod : formData.PaymentMethod,
                    paymentKeys:{
                        stripeSecretKey: formData.stripeSecretKey.trim(),
                        stripePublicKey: formData.stripePublicKey.trim()
                    }
                });
            }
            else
            {
                setAdminFormData({
                    carwashid: carwashid,
                    PaymentMethod : formData.PaymentMethod,
                    paymentKeys:{
                        stripeSecretKey: formData.stripeSecretKey.trim(),
                        stripePublicKey: formData.stripePublicKey.trim()
                    }
                });
            }
        }
    }, [formData, carwashid, cuserType]);

    const saveBankInfo = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (formData.PaymentMethod === 1 && hasInvalidStripeSecretKey(formData.stripeSecretKey)) {
            toaster.toastError(STRIPE_SECRET_KEY_LIVE_ERROR);
            return;
        }
        try {
            // Validate form data
            await addBankInfoSchema.validate(formData, { abortEarly: false });
                try 
                {
                    setLoading(true); // Show loader
                        const responseLocationBankUpdate = cuserType === userType['CARWASH-OWNER'] ?
                        await axios.post(CompanyApi.CARWASH_OWNER_UPDATE_PAYMENT_METHOD, ownerformData) :
                        await axios.post(CompanyApi.UPDATE_PAYMENT_METHOD, adminformData) ;

                        if (responseLocationBankUpdate?.data?.error === 0) {
                            //Clear errors
                            setErrors({
                                PaymentMethod: '',
                                stripeSecretKey: '',
                                stripePublicKey: ''
                            });

                            // Save form data to localStorage
                            localStorage.setItem('bankInformation', JSON.stringify(formData));
                            toaster.toastSuccess("Bank details added successfully.");
                            onComplete();

                        }
                        else {
                            toaster.toastError(responseLocationBankUpdate?.data?.message);
                        }
                    
                } catch (error) {
                    console.log(error);
                    toaster.toastError('Failed to add Bank Details.');
                } finally {
                    setLoading(false); // Hide loader
                }
        } catch (error) {
            // If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                const newErrors: any = {};
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }
        }
    }

    return (
        <div className='step-content-outer'>
            <form onSubmit={(e) => saveBankInfo(e)}>
                <div className='common-wrap bank-info-wrapper'>
                    <div className="title-block mx-auto text-center">
                        <h3 className='f-bold'>Merchant Payment Information</h3>
                        {/* <p className='f-medium'>Add your bank details for each location to
                            process customer payments easily.</p> */}
                    </div>
                    <div className="form-inner mx-auto">
                        <CWSelect
                            id="PaymentMethod"
                            name="PaymentMethod"
                            options={paymentOptions}
                            label="Select Merchant Payment Type"
                            errorMessage={errors.PaymentMethod && errors.PaymentMethod}
                            dropdownGap={0}
                            dropdownPosition="auto"
                            values={paymentOptions.filter(option => option.value === formData.PaymentMethod)}
                            onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'PaymentMethod')}
                            placeholder="Select Merchant Payment Type"
                            multi={false}
                            searchable={false}
                            isMandatory
                        />
                        {formData.PaymentMethod === 1 && (
                        <>
                            <CWInput
                                id="stripeSecretKey"
                                name="stripeSecretKey"
                                type="text"
                                label={stripeSecretKeyCwInputLabel}
                                value={formData.stripeSecretKey}
                                onChange={handleChange}
                                errorMessage={errors.stripeSecretKey}
                                ref={(el) => (refs.current.stripeSecretKey = el)}
                                autoComplete="off"
                                isMandatory
                                autoFocus
                            />
                            <CWInput
                                id="stripePublicKey"
                                name="stripePublicKey"
                                type="text"
                                label={stripePublicKeyCwInputLabel}
                                value={formData.stripePublicKey}
                                onChange={handleChange}
                                errorMessage={errors.stripePublicKey}
                                isMandatory
                                ref={(el) => (refs.current.stripePublicKey = el)}
                            />
                        </>
                        )}
                        {formData.PaymentMethod === 2 && (
                        <>
                        {/* use when world pay is ready */}
                            <CWInput
                                id="SecretKey"
                                name="SecretKey"
                                type="text"
                                label="SecretKey"
                                onChange={handleChange}
                                // value={formData.SecretKey}
                                // errorMessage={errors.SecretKey}
                                // ref={(el) => (refs.current.SecretKey = el)}
                                autoComplete="off"
                                isMandatory
                                autoFocus
                            />
                            <CWInput
                                id="PublicKey"
                                name="PublicKey"
                                type="text"
                                label="Account Name"
                                onChange={handleChange}
                                // value={formData.PublicKey}
                                // errorMessage={errors.PublicKey}
                                // ref={(el) => (refs.current.PublicKey = el)}
                                isMandatory
                            />
                        </>
                        )}
                    </div>
                </div>
                <div className='btn-action-sec d-flex align-items-center justify-content-between'>
                    <Button title="Save" variant="primary" className="primary-btn ms-auto" type='submit'>Save</Button>
                </div>
            </form>
            {loading && <FullLoader />}
        </div>
    );
};
export default BankInformation;
